import React, { useMemo } from "react";
import { Skeleton, Box, Stack } from "@mui/material";
import { motion, Variants } from "framer-motion";
import { useReducedMotion } from "../../hooks/useReducedMotion";
import { LoaderContainer, LoaderTitle, LoaderSubtitle } from "./styles";

export interface SkeletonConfig {
  maxWidth?: string;
  spacing?: number;
  borderRadius?: string;
  animationType?: "wave" | "pulse" | false;
}

export interface KLSkeletonLoaderProps {
  /**
   * Text to display below the skeleton animation
   */
  text: string;
  /**
   * Type of skeleton to display
   * - "list": Shows multiple card-like skeletons (for lists of items)
   * - "form": Shows form-like skeletons (for form inputs)
   * - "card": Shows a single card skeleton
   * - "simple": Shows just a spinner with text (minimal)
   */
  variant?: "list" | "form" | "card" | "simple";
  /**
   * Number of skeleton items to show (for "list" variant)
   */
  count?: number;
  /**
   * Configuration for skeleton dimensions and styling
   */
  config?: SkeletonConfig;
}

// Animation variants defined outside component to prevent recreation on every render
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08, // Slightly faster for better choreography
      delayChildren: 0.15,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1] as const,
    },
  },
};

// Title animation with gentle pulse
const titleVariants: Variants = {
  hidden: { opacity: 0, y: -10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1] as const,
    },
  },
};

// Separate pulse animation for continuous effect
const titlePulse = {
  scale: [1, 1.02, 1],
  transition: {
    duration: 2,
    repeat: Infinity,
    ease: "easeInOut" as const,
  },
};

// Subtitle fade in
const subtitleVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.5,
      delay: 0.2,
    },
  },
};

const KLSkeletonLoader: React.FC<KLSkeletonLoaderProps> = ({
  text,
  variant = "simple",
  count = 3,
  config = {},
}) => {
  const reducedMotion = useReducedMotion();

  // Destructure config with defaults
  const {
    maxWidth = "400px",
    spacing = 2,
    borderRadius = "12px",
    animationType = "wave",
  } = config;

  // Determine animation type based on reduced motion and config
  const skeletonAnimation = reducedMotion ? false : animationType;

  // Memoize skeleton content to prevent unnecessary re-renders
  const skeletonContent = useMemo(() => {
    switch (variant) {
      case "list":
        return (
          <Stack spacing={spacing} sx={{ width: "100%", maxWidth }}>
            {Array.from({ length: count }).map((_, index) => (
              <motion.div
                key={`skeleton-list-${index}`}
                variants={reducedMotion ? {} : itemVariants}
                initial="hidden"
                animate="visible"
              >
                <Box
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    borderRadius,
                    padding: "1rem",
                    boxShadow: "0px 2px 8px rgba(31, 38, 135, 0.08)",
                  }}
                >
                  <Skeleton
                    variant="rectangular"
                    height={60}
                    sx={{ borderRadius: `calc(${borderRadius} - 4px)`, mb: 1 }}
                    animation={skeletonAnimation}
                  />
                  <Skeleton
                    variant="text"
                    width="70%"
                    animation={skeletonAnimation}
                  />
                  <Skeleton
                    variant="text"
                    width="40%"
                    animation={skeletonAnimation}
                  />
                </Box>
              </motion.div>
            ))}
          </Stack>
        );

      case "form":
        return (
          <Stack spacing={spacing + 1} sx={{ width: "100%", maxWidth }}>
            {Array.from({ length: count }).map((_, index) => (
              <motion.div
                key={`skeleton-form-${index}`}
                variants={reducedMotion ? {} : itemVariants}
                initial="hidden"
                animate="visible"
              >
                <Skeleton
                  variant="text"
                  width="30%"
                  height={20}
                  sx={{ mb: 0.5 }}
                  animation={skeletonAnimation}
                />
                <Skeleton
                  variant="rectangular"
                  height={56}
                  sx={{ borderRadius }}
                  animation={skeletonAnimation}
                />
              </motion.div>
            ))}
            <Skeleton
              variant="rectangular"
              height={54}
              sx={{ borderRadius, mt: 2 }}
              animation={skeletonAnimation}
            />
          </Stack>
        );

      case "card":
        return (
          <motion.div
            variants={reducedMotion ? {} : itemVariants}
            initial="hidden"
            animate="visible"
          >
            <Box
              sx={{
                width: "100%",
                maxWidth,
                backgroundColor: "rgba(255, 255, 255, 0.9)",
                borderRadius,
                padding: "1.5rem",
                boxShadow: "0px 4px 16px rgba(31, 38, 135, 0.12)",
              }}
            >
              <Skeleton
                variant="rectangular"
                height={200}
                sx={{ borderRadius: `calc(${borderRadius} - 4px)`, mb: 2 }}
                animation={skeletonAnimation}
              />
              <Skeleton
                variant="text"
                width="60%"
                height={32}
                sx={{ mb: 1 }}
                animation={skeletonAnimation}
              />
              <Skeleton
                variant="text"
                width="80%"
                animation={skeletonAnimation}
              />
              <Skeleton
                variant="text"
                width="70%"
                animation={skeletonAnimation}
              />
            </Box>
          </motion.div>
        );

      case "simple":
      default:
        return (
          <Box sx={{ textAlign: "center" }}>
            <Skeleton
              variant="circular"
              width={80}
              height={80}
              sx={{ margin: "0 auto", mb: 2 }}
              animation={skeletonAnimation}
            />
            <Skeleton
              variant="text"
              width="60%"
              height={32}
              sx={{ margin: "0 auto", mb: 1 }}
              animation={skeletonAnimation}
            />
            <Skeleton
              variant="text"
              width="40%"
              height={24}
              sx={{ margin: "0 auto" }}
              animation={skeletonAnimation}
            />
          </Box>
        );
    }
  }, [variant, count, maxWidth, spacing, borderRadius, skeletonAnimation, reducedMotion]);

  // Wrap content with motion
  const renderContent = () => {
    // Skip motion wrapper if reduced motion is preferred
    if (reducedMotion) {
      return (
        <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
          {skeletonContent}
        </div>
      );
    }

    return (
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{ width: "100%", display: "flex", justifyContent: "center" }}
      >
        {skeletonContent}
      </motion.div>
    );
  };

  return (
    <LoaderContainer role="status" aria-live="polite" aria-busy="true">
      {reducedMotion ? (
        <>
          <LoaderTitle aria-label="כמה רגעים">...כמה רגעים</LoaderTitle>
          <LoaderSubtitle>{text || 'טוען...'}</LoaderSubtitle>
        </>
      ) : (
        <>
          <motion.div
            variants={titleVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div animate={titlePulse}>
              <LoaderTitle aria-label="כמה רגעים">...כמה רגעים</LoaderTitle>
            </motion.div>
          </motion.div>
          <motion.div
            variants={subtitleVariants}
            initial="hidden"
            animate="visible"
          >
            <LoaderSubtitle>{text || 'טוען...'}</LoaderSubtitle>
          </motion.div>
        </>
      )}
      {renderContent()}
    </LoaderContainer>
  );
};

export default KLSkeletonLoader;
